import axiosInstance from '../core/index-core';

function uuidv4() {
  return crypto.randomUUID();
}

function resolveClickableTarget(el) {
  if (!el || typeof el.closest !== 'function') return { el: null, type: undefined };

  const target =
    el.closest('button, a, input, textarea, select, [role="button"]') || null;

  if (!target) return { el: null, type: undefined };

  const tag = (target.tagName || '').toLowerCase();

  if (tag === 'a') return { el: target, type: 'link' };
  if (tag === 'button' || target.getAttribute('role') === 'button') {
    return { el: target, type: 'button' };
  }
  if (tag === 'input' || tag === 'textarea' || tag === 'select') {
    return { el: target, type: 'input' };
  }

  return { el: target, type: undefined };
}


export function getSessionId() {
  const KEY = 'metrics_session_id';
  let v = sessionStorage.getItem(KEY);
  if (!v) {
    v = uuidv4();
    sessionStorage.setItem(KEY, v);
  }
  return v;
}

function safeElementText(el) {
  const tag = (el?.tagName || '').toLowerCase();
  if (tag === 'input' || tag === 'textarea') return undefined;

  const candidates = [
    el?.getAttribute?.('metrics-label'),
    el?.textContent,
    el?.getAttribute?.('aria-label'),
    el?.getAttribute?.('title'),
  ];

  const t = candidates
    .map(v => (typeof v === 'string' ? v.trim() : v))
    .find(Boolean);

  return t ? t.slice(0, 200) : undefined;
}

const delay = ms => new Promise(r => setTimeout(r, ms));

export class MetricsClient {
  constructor() {
    this.events = [];
    this.maxBatchSize = 100;
    this.idleMs = 30000;
    this.timerId = null;
    this.sending = false;

    this.sessionId = getSessionId();

    window.addEventListener('beforeunload', () => {
      if (this.events.length === 0) return;
      try {
        const payload = JSON.stringify({ events: this.events });
        navigator.sendBeacon(
          '/api/metrics/clickstream',
          new Blob([payload], { type: 'application/json' })
        );
      } catch {
        console.error('Error sending leftover events')
      }
    });
  }

  add(event) {
    this.events.push(event);
    if (this.events.length >= this.maxBatchSize) {
      this.flush();
    } else {
      this._armIdle();
    }
  }

  _armIdle() {
    if (this.timerId) clearTimeout(this.timerId);
    this.timerId = setTimeout(() => this.flush(), this.idleMs);
  }

  async flush() {
    if (this.sending || this.events.length === 0) return;
    this.sending = true;
    try {
      // Drain the queue: send all accumulated events in batches.
      // This ensures that events added while a batch is in-flight
      // (e.g., domain error events flushed during a periodic send)
      // are sent immediately rather than waiting for the next trigger.
      while (this.events.length > 0) {
        const batch = this.events.splice(0, this.maxBatchSize);
        await this._sendBatch(batch);
      }
    } finally {
      this.sending = false;
    }
  }

  async _sendBatch(events) {
    try {
      // IMPORTANT: Mark the request so the Axios response interceptor in
      // index-core.js does NOT emit auth error telemetry for 401s on this
      // endpoint. Without this guard, a 401 on the metrics endpoint would
      // generate new auth error events → queued → flushed → another 401 →
      // infinite cascade of telemetry events.
      const res = await axiosInstance.post(
        '/api/metrics/clickstream',
        { events },
        { _isMetricsRequest: true },
      );

      if (res.status >= 200 && res.status < 300) return;

      if (res.status === 401) {
        console.error('[metrics] 401 Unauthorized — refresh auth');
        return;
      }
      if (res.status === 400) {
        console.error('[metrics] 400 Bad Request:', res.data);
        return;
      }

      if (res.status === 429 || res.status >= 500) {
        for (let i = 1; i <= 3; i++) {
          await delay(2 ** i * 1000);
          const retry = await axiosInstance.post(
            '/api/metrics/clickstream',
            { events },
            { _isMetricsRequest: true },
          );
          if (retry.status >= 200 && retry.status < 300) return;
        }
        console.error('[metrics] failed after retries:', res.data);
      }
    } catch (e) {
      console.error('[metrics] network error', e);
      for (let i = 1; i <= 3; i++) {
        await delay(2 ** i * 1000);
        try {
          const retry = await axiosInstance.post(
            '/api/metrics/clickstream',
            { events },
            { _isMetricsRequest: true },
          );
          if (retry.status >= 200 && retry.status < 300) return;
        } catch {
          console.error('Failed sending clickstream');
        }
      }
    }
  }



clickEvent(target, extra = {}) {
  const { el: resolved, type } = resolveClickableTarget(target);

  const src = resolved || target;

  return {
    id: uuidv4(),
    session_id: this.sessionId,
    event_type: 'click',
    timestamp: new Date().toISOString(),
    element_type: type || undefined,
    element_id: src?.id || undefined,
    element_text: safeElementText(src),
    page_url: window.location.href,
    page_path: window.location.pathname,
    page_title: document.title,
    referrer: document.referrer || undefined,
    screen_resolution: `${screen.width}x${screen.height}`,
    viewport_size: `${window.innerWidth}x${window.innerHeight}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    language: navigator.language,
    metadata: extra && Object.keys(extra).length ? extra : undefined,
  };
}


  apiCallEvent({ method, endpoint, statusCode, durationMs }) {
    return {
      id: uuidv4(),
      session_id: this.sessionId,
      event_type: 'api_call',
      timestamp: new Date().toISOString(),
      http_method: method,
      endpoint,
      status_code: statusCode,
      duration_ms: durationMs,
      page_path: window.location.pathname,
      page_title: document.title,
    };
  }

  errorEvent(errorObj) {
    return {
      id: uuidv4(),
      session_id: this.sessionId,
      event_type: 'error',
      timestamp: new Date().toISOString(),
      page_path: window.location.pathname,
      page_title: document.title,
      metadata: {
        error_type: errorObj?.name || 'Error',
        error_message: String(errorObj?.message || errorObj),
        stack_trace: errorObj?.stack,
      },
    };
  }

  navigationEvent(path, extra = {}) {
    return {
      id: uuidv4(),
      session_id: this.sessionId,
      event_type: 'navigation',
      timestamp: new Date().toISOString(),
      page_path: path,
      page_title: document.title,
      page_url: window.location.href,
      referrer: document.referrer || undefined,
      metadata: Object.keys(extra).length ? extra : undefined,
    };
  }

  scrollEvent({ pct }) {
    return {
      id: uuidv4(),
      session_id: this.sessionId,
      event_type: 'scroll',
      timestamp: new Date().toISOString(),
      page_path: window.location.pathname,
      page_title: document.title,
      metadata: { percent: Math.round(pct) },
    };
  }

  visibilityEvent({ name, visible }) {
    return {
      id: uuidv4(),
      session_id: this.sessionId,
      event_type: 'visibility',
      timestamp: new Date().toISOString(),
      page_path: window.location.pathname,
      page_title: document.title,
      metadata: { name, visible: !!visible },
    };
  }

  // ---------------------------------------------------------------------------
  // Domain-specific error events (Frontend Logging Gap Analysis)
  // ---------------------------------------------------------------------------

  /**
   * Emit when "Couldn't start AI notes" dialog or any note generation failure is shown.
   */
  noteGenerationErrorEvent({ session_id, patient_id, reason, attempt_count, error_code } = {}) {
    return {
      id: uuidv4(),
      trace_id: uuidv4(),
      session_id: this.sessionId,
      event_type: 'error.note_generation',
      timestamp: new Date().toISOString(),
      page_path: window.location.pathname,
      page_title: document.title,
      metadata: {
        session_id,
        patient_id,
        reason: reason ? String(reason) : undefined,
        attempt_count,
        error_code,
      },
    };
  }

  /**
   * Emit when recording is interrupted — AudioContext suspended, WebSocket
   * disconnected during recording, or reconnection exhausted.
   */
  recordingInterruptedEvent({ session_id, patient_id, duration_before_failure, cause } = {}) {
    return {
      id: uuidv4(),
      trace_id: uuidv4(),
      session_id: this.sessionId,
      event_type: 'error.recording_interrupted',
      timestamp: new Date().toISOString(),
      page_path: window.location.pathname,
      page_title: document.title,
      metadata: {
        session_id,
        patient_id,
        duration_before_failure,
        cause: cause ? String(cause) : undefined,
      },
    };
  }

  /**
   * Emit when EHR submit (AttestationModal) fails.
   */
  ehrSubmitErrorEvent({ session_id, patient_id, note_id, error_message } = {}) {
    return {
      id: uuidv4(),
      trace_id: uuidv4(),
      session_id: this.sessionId,
      event_type: 'error.ehr_submit',
      timestamp: new Date().toISOString(),
      page_path: window.location.pathname,
      page_title: document.title,
      metadata: {
        session_id,
        patient_id,
        note_id,
        error_message: error_message ? String(error_message) : undefined,
      },
    };
  }

  /**
   * Emit when MSAL token acquisition fails or "Not authorized" is shown.
   */
  authErrorEvent({ error_type, silent_or_interactive, user_id, error_message } = {}) {
    return {
      id: uuidv4(),
      trace_id: uuidv4(),
      session_id: this.sessionId,
      event_type: 'error.auth',
      timestamp: new Date().toISOString(),
      page_path: window.location.pathname,
      page_title: document.title,
      metadata: {
        error_type,
        silent_or_interactive,
        user_id,
        error_message: error_message ? String(error_message) : undefined,
      },
    };
  }

  /**
   * Emit on WebSocket close/error during an active session.
   */
  websocketErrorEvent({ close_code, close_reason, was_recording, session_id, patient_id, cause } = {}) {
    return {
      id: uuidv4(),
      trace_id: uuidv4(),
      session_id: this.sessionId,
      event_type: 'error.websocket',
      timestamp: new Date().toISOString(),
      page_path: window.location.pathname,
      page_title: document.title,
      metadata: {
        close_code,
        close_reason,
        was_recording: !!was_recording,
        session_id,
        patient_id,
        cause: cause ? String(cause) : undefined,
      },
    };
  }

  /**
   * Emit on Page Visibility API change (tab backgrounded / foregrounded).
   */
  pageVisibilityChangeEvent({ visible, was_recording, duration_hidden, session_id, patient_id } = {}) {
    return {
      id: uuidv4(),
      session_id: this.sessionId,
      event_type: 'page.visibility_change',
      timestamp: new Date().toISOString(),
      page_path: window.location.pathname,
      page_title: document.title,
      metadata: {
        visible: !!visible,
        was_recording: !!was_recording,
        duration_hidden,
        session_id,
        patient_id,
      },
    };
  }

  // ---------------------------------------------------------------------------
  // IntersectionObserver management for visibility tracking
  // ---------------------------------------------------------------------------
  // The observer is created in MetricsProvider.jsx and connected here via
  // setObserver(). Components use the useVisibilityTracking hook which calls
  // observe/unobserve on this singleton.

  /**
   * Set the IntersectionObserver instance (called by MetricsProvider on mount).
   */
  setObserver(observer) {
    this._observer = observer;
  }

  /**
   * Register an element for visibility tracking via IntersectionObserver.
   * The element should have a `data-metrics-name` attribute for identification.
   */
  observe(el) {
    this._observer?.observe(el);
  }

  /**
   * Unregister an element from visibility tracking.
   */
  unobserve(el) {
    this._observer?.unobserve(el);
  }

  // ---------------------------------------------------------------------------
  // Recording lifecycle events (WebSocket Workflow Telemetry Audit)
  // ---------------------------------------------------------------------------

  /**
   * Emit when recording starts successfully (WebSocket opens, audio flowing).
   */
  recordingStartedEvent({ session_id, patient_id, had_previous_recording } = {}) {
    return {
      id: uuidv4(),
      session_id: this.sessionId,
      event_type: 'recording.started',
      timestamp: new Date().toISOString(),
      page_path: window.location.pathname,
      page_title: document.title,
      metadata: {
        session_id,
        patient_id,
        had_previous_recording: !!had_previous_recording,
      },
    };
  }

  /**
   * Emit when recording is stopped by the user.
   */
  recordingStoppedEvent({ session_id, patient_id, duration_seconds } = {}) {
    return {
      id: uuidv4(),
      session_id: this.sessionId,
      event_type: 'recording.stopped',
      timestamp: new Date().toISOString(),
      page_path: window.location.pathname,
      page_title: document.title,
      metadata: {
        session_id,
        patient_id,
        duration_seconds,
      },
    };
  }

  /**
   * Emit when recording is paused or resumed.
   * @param {string} action - 'paused' or 'resumed'
   * @param {string} trigger - 'user' | 'disconnect' | 'reconnect' | 'network_lost' | 'network_restored'
   */
  recordingPauseResumeEvent({ session_id, patient_id, action, trigger, duration_seconds } = {}) {
    return {
      id: uuidv4(),
      session_id: this.sessionId,
      event_type: 'recording.pause_resume',
      timestamp: new Date().toISOString(),
      page_path: window.location.pathname,
      page_title: document.title,
      metadata: {
        session_id,
        patient_id,
        action,
        trigger,
        duration_seconds,
      },
    };
  }

  /**
   * Emit when a same-day recording is recovered from a previous session.
   */
  recordingRecoveredEvent({ session_id, patient_id, char_count, timestamp_offset } = {}) {
    return {
      id: uuidv4(),
      session_id: this.sessionId,
      event_type: 'recording.recovered',
      timestamp: new Date().toISOString(),
      page_path: window.location.pathname,
      page_title: document.title,
      metadata: {
        session_id,
        patient_id,
        char_count,
        timestamp_offset,
      },
    };
  }

  /**
   * Emit when no transcript activity is detected for an extended period.
   */
  transcriptInactivityEvent({ session_id, patient_id, inactivity_count, timeout_seconds } = {}) {
    return {
      id: uuidv4(),
      session_id: this.sessionId,
      event_type: 'recording.transcript_inactivity',
      timestamp: new Date().toISOString(),
      page_path: window.location.pathname,
      page_title: document.title,
      metadata: {
        session_id,
        patient_id,
        inactivity_count,
        timeout_seconds,
      },
    };
  }

  /**
   * Emit on heartbeat timeout (before WS close is triggered).
   */
  heartbeatTimeoutEvent({ session_id, patient_id, elapsed_seconds, last_pong_ago } = {}) {
    return {
      id: uuidv4(),
      session_id: this.sessionId,
      event_type: 'recording.heartbeat_timeout',
      timestamp: new Date().toISOString(),
      page_path: window.location.pathname,
      page_title: document.title,
      metadata: {
        session_id,
        patient_id,
        elapsed_seconds,
        last_pong_ago,
      },
    };
  }

  /**
   * Emit when AWS Transcribe stream ends naturally (code 4002) and seamless reconnect starts.
   */
  transcribeStreamEndedEvent({ session_id, patient_id } = {}) {
    return {
      id: uuidv4(),
      session_id: this.sessionId,
      event_type: 'recording.transcribe_stream_ended',
      timestamp: new Date().toISOString(),
      page_path: window.location.pathname,
      page_title: document.title,
      metadata: {
        session_id,
        patient_id,
      },
    };
  }

  /**
   * Emit on network status change during recording.
   * @param {string} status - 'lost' or 'restored'
   */
  networkStatusEvent({ session_id, patient_id, status, ws_was_alive } = {}) {
    return {
      id: uuidv4(),
      session_id: this.sessionId,
      event_type: 'recording.network_status',
      timestamp: new Date().toISOString(),
      page_path: window.location.pathname,
      page_title: document.title,
      metadata: {
        session_id,
        patient_id,
        status,
        ws_was_alive: ws_was_alive !== undefined ? !!ws_was_alive : undefined,
      },
    };
  }

  /**
   * Emit on microphone state changes (OS-level mute/unmute, disconnect, reconnect).
   * @param {string} event_name - 'muted' | 'unmuted' | 'disconnected' | 'reconnected' | 'reconnect_failed'
   */
  microphoneEvent({ session_id, patient_id, event_name, detail } = {}) {
    return {
      id: uuidv4(),
      session_id: this.sessionId,
      event_type: 'recording.microphone',
      timestamp: new Date().toISOString(),
      page_path: window.location.pathname,
      page_title: document.title,
      metadata: {
        session_id,
        patient_id,
        event_name,
        detail: detail ? String(detail) : undefined,
      },
    };
  }

  /**
   * Emit when AudioWorklet is not available and ScriptProcessor fallback is used,
   * or when AudioContext is resumed by the watchdog after suspension.
   * @param {string} event_name - 'worklet_fallback' | 'context_resumed_by_watchdog'
   */
  audioProcessingEvent({ session_id, patient_id, event_name, detail } = {}) {
    return {
      id: uuidv4(),
      session_id: this.sessionId,
      event_type: 'recording.audio_processing',
      timestamp: new Date().toISOString(),
      page_path: window.location.pathname,
      page_title: document.title,
      metadata: {
        session_id,
        patient_id,
        event_name,
        detail: detail ? String(detail) : undefined,
      },
    };
  }

  // ---------------------------------------------------------------------------
  // Transport quality events (WebSocket HOL blocking detection)
  // ---------------------------------------------------------------------------

  /**
   * Emit when transcript delivery appears bursty — a gap longer than 2x
   * expected interval followed by multiple segments in quick succession.
   * This is the client-side signal for TCP head-of-line blocking.
   */
  transcriptStallEvent({ session_id, patient_id, gap_ms, expected_ms, buffered_count } = {}) {
    return {
      id: uuidv4(),
      session_id: this.sessionId,
      event_type: 'transport.transcript_stall',
      timestamp: new Date().toISOString(),
      page_path: window.location.pathname,
      page_title: document.title,
      metadata: {
        session_id,
        patient_id,
        gap_ms,
        expected_ms,
        buffered_count,
      },
    };
  }

  /**
   * Emit when WebSocket.bufferedAmount exceeds threshold during recording,
   * indicating the network cannot keep up with audio chunk production.
   */
  wsBackpressureEvent({ session_id, patient_id, buffered_bytes, recording_duration_seconds } = {}) {
    return {
      id: uuidv4(),
      session_id: this.sessionId,
      event_type: 'transport.ws_backpressure',
      timestamp: new Date().toISOString(),
      page_path: window.location.pathname,
      page_title: document.title,
      metadata: {
        session_id,
        patient_id,
        buffered_bytes,
        recording_duration_seconds,
      },
    };
  }

  /**
   * Emit when WebSocket reconnection completes, with timing for the gap.
   */
  wsReconnectDurationEvent({ session_id, patient_id, duration_ms, attempt_number, audio_gap_ms } = {}) {
    return {
      id: uuidv4(),
      session_id: this.sessionId,
      event_type: 'transport.ws_reconnect_duration',
      timestamp: new Date().toISOString(),
      page_path: window.location.pathname,
      page_title: document.title,
      metadata: {
        session_id,
        patient_id,
        duration_ms,
        attempt_number,
        audio_gap_ms,
      },
    };
  }

  /**
   * Periodic sample of transcript delivery smoothness.
   * Emitted every 30s during an active recording with aggregated stats.
   */
  transcriptDeliveryStatsEvent({ session_id, patient_id, window_seconds, segments_received, mean_gap_ms, max_gap_ms, stall_count, burst_count } = {}) {
    return {
      id: uuidv4(),
      session_id: this.sessionId,
      event_type: 'transport.transcript_delivery_stats',
      timestamp: new Date().toISOString(),
      page_path: window.location.pathname,
      page_title: document.title,
      metadata: {
        session_id,
        patient_id,
        window_seconds,
        segments_received,
        mean_gap_ms,
        max_gap_ms,
        stall_count,
        burst_count,
      },
    };
  }

  /**
   * Emit when idle detection triggers a session timeout.
   */
  sessionTimeoutEvent({ idle_duration, had_active_recording } = {}) {
    return {
      id: uuidv4(),
      session_id: this.sessionId,
      event_type: 'session.timeout',
      timestamp: new Date().toISOString(),
      page_path: window.location.pathname,
      page_title: document.title,
      metadata: {
        idle_duration,
        had_active_recording: !!had_active_recording,
      },
    };
  }
}

export const metrics = new MetricsClient();
